import {
	FundProductStatusesEnum,
	FundProductTypesEnum,
	Language,
	ProductFieldNames,
	ProductPropertiesFieldNames,
	ProductSubType,
	ReportExtensions
} from '@spa/api';
import {Helper, RadioGroupControl} from 'src/app/shared/classes';
import {ComponentTypesEnum} from 'src/app/shared/enums';
import {IProductControls, IProductTableColumns, Product, ProductFilter} from './products.models';
import {LanguageService} from "../../../../shared/language.service";
import {RadioGroupOptionInterface} from "../../../../shared/components/radio-group/interfaces";

export const GetProductTableColumns = (isPreview: boolean, subType: ProductSubType, type?: FundProductTypesEnum): IProductTableColumns => {
	const isPrototype = Helper.isPrototypePortal();

	if (isPrototype) {
		const columns: IProductTableColumns = {
			underlyings: true,
			ccy: true,
			durationInMonths: true,
			subscriptionPeriodEndsOn: true,
		};

		if (isPreview) {
			if (subType === ProductSubType.NotAvailable) {
				columns.annualInterestRate = true;
				columns.barrier = true;
				columns.strikeLevel = true;
				columns.capitalProtection = true;
			} else {
				columns.annualInterestRate = SubTypeCoupon.indexOf(subType) >= 0;
				columns.barrier = SubTypeBarrier.indexOf(subType) >= 0;
				columns.strikeLevel = SubTypeStrikeLevel.indexOf(subType) >= 0;
				columns.memoryEffect = SubTypeMemoryEffect.indexOf(subType) >= 0;
				columns.capLevel = SubTypeCapLevel.indexOf(subType) >= 0;
				columns.bonusLevel = SubTypeBonusLevel.indexOf(subType) >= 0;
				columns.warrantType = SubTypeWarrantType.indexOf(subType) >= 0;
				columns.expiresOn = SubTypeExpiresOn.indexOf(subType) >= 0;
				columns.conversionRatio = SubTypeConversationRatio.indexOf(subType) >= 0;
				columns.capitalProtection = SubTypeCapitalProtection.indexOf(subType) >= 0;
				columns.participation = SubTypeParticipation.indexOf(subType) >= 0;
			}
		} else {
			columns.version = true;
			columns.subType = true;
			columns.expiresOn = true;
		}

		return columns;
	} else {
		if (type === FundProductTypesEnum.MutualFund) {
			return {
				name: true,
				type: true,
				yearToDateYield: true,
				trailingTwelveMonthsYield: true,
				netAssetValue: true,
				ccy: true,
			};
		} else {
			return {
				name: true,
				subType: true,
				annualizedYieldToMaturity: true,
				ccy: true,
				expiresOn: true,
				annualInterestRate: true
			};
		}

	}

};

export const GetProductFilterControls = (isPreview: boolean, languageService: LanguageService): IProductControls => {
	const getControl = (name: string, placeholder: string, value?: string): RadioGroupControl => {
		return new RadioGroupControl({
			name,
			placeholder,
			value,
			validators: [],
			componentType: ComponentTypesEnum.RadioGroup,
			options: []
		});
	};

	const translate = (key: string) => {
		return languageService.getInstantTranslationByKey(key);
	}

	const statusValue = isPreview
		? FundProductStatusesEnum.InSubscription.toString()
		: null;

	const typeValue = FundProductTypesEnum.Bonds.toString();
	const subTypeValue = ProductSubType.GovernmentBonds.toString();

	return {
		navigation: { options: [] },
		type: getControl(ProductFieldNames.Type.toString(), 'Product Type', typeValue),
		status: getControl(ProductFieldNames.Status, 'Product by Status', statusValue),
		issuedBy: getControl(ProductFieldNames.IssuedBy, translate('PRODUCTS.FILTER.BY_ISSUER')),
		category: getControl(ProductFieldNames.Category, 'Category'),
		group: getControl(ProductFieldNames.Group, 'Group'),
		subType: getControl(ProductFieldNames.SubType, translate('PRODUCTS.FILTER.SUB_TYPE'), subTypeValue),
		annualInterestRate: getControl(ProductFieldNames.AnnualInterestRate, 'Coupon'),
		expiresOn: getControl(ProductFieldNames.ExpiresOn, translate('PRODUCTS.FILTER.MATURITY')),
		capitalProtection: getControl(ProductFieldNames.CapitalProtection, 'Protection'),
		barrier: getControl(ProductFieldNames.Barrier, 'Distance to Barrier'),
		annualizedYieldToMaturity: getControl(ProductPropertiesFieldNames.AnnualizedYieldToMaturity, translate('PRODUCTS.FILTER.BY_YIELD')),
	};
};

export const GetProductMaturityOptions = (language: string): RadioGroupOptionInterface[] => {

	const months = language === Language.English
		? 'months'
		: 'м.';


	const years = language === Language.English
		? 'years'
		: 'г.';

	return [
		{
			label: `< 6 ${months}`,
			value: 0
		},
		{
			label: `6 - 12 ${months}`,
			value: 1
		},
		{
			label: `1 - 2 ${years}`,
			value: 2
		},
		{
			label: `2 - 3 ${years}`,
			value: 3
		},
		{
			label: `3 - 5 ${years}`,
			value: 4
		},
		{
			label: `> 5 ${years}`,
			value: 5
		}];
}

export const InitProductFilterOptions = (
	language: Language,
	isPreview: boolean,
	products: Product[],
	controls: IProductControls,
	filter: ProductFilter): void => {
	const onlyUnique = (value: any, index: any, self: any) => {
		return self.indexOf(value) === index;
	};

	controls.navigation.options.splice(0);
	controls.type.options.splice(0);
	controls.status.options.splice(0);
	controls.issuedBy.options.splice(0);
	controls.category.options.splice(0);
	controls.group.options.splice(0);
	controls.subType.options.splice(0);
	controls.annualInterestRate.options.splice(0);
	controls.expiresOn.options.splice(0);
	controls.capitalProtection.options.splice(0);
	controls.barrier.options.splice(0);
	controls.annualizedYieldToMaturity.options.splice(0);

	const textHelper = Helper.textsHelper();

	const subTypes = products.map(s => s.subType)
	products
		.filter((product, index, self) => {
			return subTypes.indexOf(product.subType) === index;
		})
		.sort((a, b) => {
			return b.type - a.type + a.subType - b.subType
		})
		.forEach(product => {
			controls.navigation.options.push({
				label: textHelper.getProductTypeOrSubTypeName(product.type, product.subType, language),
				value: {type: product.type, subType: product.subType}
			});
		});

	products.map(product => product.type)
		.filter(onlyUnique)
		.sort((a, b) => {
			return b - a
		})
		.forEach(value => {
			controls.type.options.push({
				label: textHelper.getProductTypeName(value, language),
				value
			});
		});

	products.map(product => product.status)
		.filter(onlyUnique)
		.forEach(value => {
			controls.status.options.push({
				label: textHelper.getProductStatusName(value, language),
				value
			});
		});

	if (!isPreview || filter.subType?.length > 0) {
		products.map(product => product.issuedBy)
			.filter(onlyUnique)
			.sort()
			.forEach(value => {
				if (value) {
					controls.issuedBy.options.push({
						label: value,
						value
					});
				}
			});
	}

	const yieldOptions = [
		{
			label: '< 2.00%',
			value: 0
		},
		{
			label: '2.00% - 2.99%',
			value: 1
		},
		{
			label: '3.00% - 3.99%',
			value: 2
		},
		{
			label: '> 4.00%',
			value: 3
		}];

	yieldOptions.forEach(option => {
		controls.annualizedYieldToMaturity.options.push(option);
	});

	products.map(product => product.category)
		.filter(onlyUnique)
		.forEach(value => {
			if (value) {
				controls.category.options.push({
					label: textHelper.getProductCategoryName(value),
					value
				});
			}
		});

	products.map(product => product.group)
		.filter(onlyUnique)
		.forEach(value => {
			if (value) {
				controls.group.options.push({
					label: textHelper.getProductGroupName(value),
					value
				});
			}
		});

	products.map(product => product.subType)
		.filter(onlyUnique)
		.forEach(value => {
			if (value) {
				controls.subType.options.push({
					label: textHelper.getProductSubTypeName(value, language),
					value
				});
			}
		});

	const couponOptions = [{
		label: '< 0%',
		value: 0
	},
		{
			label: '0% - 1%',
			value: 1
		},
		{
			label: '1% - 2%',
			value: 2
		},
		{
			label: '2% - 3%',
			value: 3
		},
		{
			label: '3% - 4%',
			value: 4
		},
		{
			label: '4% - 5%',
			value: 5
		},
		{
			label: '> 5%',
			value: 6
		}];

	couponOptions.forEach(option => {
		controls.annualInterestRate.options.push(option);
	});

	const maturityOptions = GetProductMaturityOptions(language);

	maturityOptions.forEach(option => {
		controls.expiresOn.options.push(option);
	});

	const protectionOptions = [{
		label: 'No protection',
		value: 0
	},
		{
			label: '< 100%',
			value: 1
		},
		{
			label: '> 100%',
			value: 2
		}];

	protectionOptions.forEach(option => {
		controls.capitalProtection.options.push(option);
	});

	const barrierOptions = [{
		label: '< 0%',
		value: 0
	},
		{
			label: '0% - 10%',
			value: 1
		},
		{
			label: '10% - 20%',
			value: 2
		},
		{
			label: '20% - 30%',
			value: 3
		},
		{
			label: '30% - 40%',
			value: 4
		},
		{
			label: '40% - 50%',
			value: 5
		},
		{
			label: '> 50%',
			value: 6
		}];

	barrierOptions.forEach(option => {
		controls.barrier.options.push(option);
	});
};

const SubTypeCoupon = [
	ProductSubType.MultiBarrierDiscountCertificate,
	ProductSubType.AutocallableMultiBarrierReverseConvertible,
	ProductSubType.BarrierReverseConvertible,
	ProductSubType.CallableMultiReverseConvertible,
	ProductSubType.CallableBarrierReverseConvertible,
	ProductSubType.LockInCertificate,
	ProductSubType.MultiBarrierReverseConvertible,
	ProductSubType.AutocallableMultiReverseConvertible,
	ProductSubType.AutocallableReverseConvertible,
	ProductSubType.CallableMultiReverseConvertible,
	ProductSubType.CallableReverseConvertible,
	ProductSubType.MultiReverseConvertible,
	ProductSubType.CouponMultiBonusCertificate,
	ProductSubType.ReferenceEntityCertificatewithConditionalCapitalProtection,
];

const SubTypeBarrier = [
	ProductSubType.BarrierDiscountCertificate,
	ProductSubType.MultiBarrierDiscountCertificate,
	ProductSubType.AutocallableMultiBarrierReverseConvertible,
	ProductSubType.BarrierReverseConvertible,
	ProductSubType.CallableMultiBarrierReverseConvertible,
	ProductSubType.CallableBarrierReverseConvertible,
	ProductSubType.LockInCertificate,
	ProductSubType.MultiBarrierReverseConvertible,
	ProductSubType.ConditionalCouponBarrierReverseConvertible,
	ProductSubType.ConditionalCouponMultiBarrierReverseConvertible,
	ProductSubType.MultiExpressCertificatewithBarrier,
	ProductSubType.BonusCertificate,
	ProductSubType.CappedBonusCertificate,
	ProductSubType.CappedMultiBonusCertificate,
	ProductSubType.CouponMultiBonusCertificate,
	ProductSubType.MultiBonusCertificate,
	ProductSubType.MultiBonusOutperformanceCertificate,
	ProductSubType.MiscellaneousParticipationCertificate,
	ProductSubType.CapitalProtectionCertificatewithBarrier,
];

const SubTypeStrikeLevel = [
	ProductSubType.BarrierDiscountCertificate,
	ProductSubType.MultiBarrierDiscountCertificate,
	ProductSubType.AutocallableMultiBarrierReverseConvertible,
	ProductSubType.CallableMultiBarrierReverseConvertible,
	ProductSubType.CallableBarrierReverseConvertible,
	ProductSubType.ConditionalCouponMultiReverseConvertible,
	ProductSubType.MiscellaneousYieldEnhancementCertificates,
	ProductSubType.AutocallableMultiReverseConvertible,
	ProductSubType.AutocallableReverseConvertible,
	ProductSubType.CallableMultiReverseConvertible,
	ProductSubType.CallableReverseConvertible,
	ProductSubType.MultiReverseConvertible,
	ProductSubType.ReverseConvertible,
	ProductSubType.MiniFuture,
	ProductSubType.Warrant,
	ProductSubType.CapitalProtectionCertificatewithBarrier,
];

const SubTypeMemoryEffect = [
	ProductSubType.ConditionalCouponBarrierReverseConvertible,
	ProductSubType.ConditionalCouponMultiBarrierReverseConvertible,
	ProductSubType.ConditionalCouponMultiReverseConvertible,
	ProductSubType.MultiExpressCertificatewithBarrier,
];

const SubTypeCapLevel = [
	ProductSubType.DiscountCertificate,
	ProductSubType.MiscellaneousYieldEnhancementCertificates,
	ProductSubType.CappedBonusCertificate,
	ProductSubType.CappedMultiBonusCertificate,
	ProductSubType.CappedOutperformanceCertificate,
	ProductSubType.CapitalProtectedCertificatewithCap,
	ProductSubType.CapitalProtectedCertificatewithoutCap,
];

const SubTypeBonusLevel = [
	ProductSubType.BonusCertificate,
	ProductSubType.CappedBonusCertificate,
	ProductSubType.CappedMultiBonusCertificate,
	ProductSubType.CouponMultiBonusCertificate,
	ProductSubType.MultiBonusCertificate,
	ProductSubType.MultiBonusOutperformanceCertificate,
	ProductSubType.MiscellaneousParticipationCertificate,
];

const SubTypeWarrantType = [
	ProductSubType.MiniFuture,
	ProductSubType.Warrant,
];

const SubTypeExpiresOn = [
	ProductSubType.Warrant,
];

const SubTypeConversationRatio = [
	ProductSubType.TrackerCertificate,
	ProductSubType.ReferenceEntityCertificatewithConditionalCapitalProtection,
];

const SubTypeCapitalProtection = [
	ProductSubType.MiscellaneousYieldEnhancementCertificates,
	ProductSubType.AutocallableReverseConvertible,
	ProductSubType.CallableReverseConvertible,
	ProductSubType.MultiReverseConvertible,
	ProductSubType.CappedMultiBonusCertificate,
	ProductSubType.CapitalProtectionCertificatewithBarrier,
	ProductSubType.AutocallablesCapitalProtectionCertificatewithCoupon,
	ProductSubType.CapitalProtectionCertificatewithCoupon,
	ProductSubType.CapitalProtectedCertificatewithCap,
	ProductSubType.CapitalProtectedCertificatewithoutCap,
];

const SubTypeParticipation = [
	ProductSubType.ConditionalCouponBarrierReverseConvertible,
	ProductSubType.MiscellaneousYieldEnhancementCertificates,
	ProductSubType.AutocallableReverseConvertible,
	ProductSubType.MultiReverseConvertible,
	ProductSubType.CappedBonusCertificate,
	ProductSubType.CouponMultiBonusCertificate,
	ProductSubType.MultiBonusOutperformanceCertificate,
	ProductSubType.MiscellaneousParticipationCertificate,
	ProductSubType.CappedOutperformanceCertificate,
	ProductSubType.OutperformanceCertificate,
	ProductSubType.MiniFuture,
	ProductSubType.CapitalProtectionCertificatewithBarrier,
	ProductSubType.CapitalProtectedCertificatewithCap,
	ProductSubType.CapitalProtectedCertificatewithoutCap,
];
